import { useState } from "react";

import { Decimal } from "classes/DecimalClasses";

import { DeleteLine } from "components/EditPage/DeleteLine";
import { QuickAddItem } from "components/QuickAddItem";
import { LineTableCell } from "components/formFields/LineTableCell";
import { LineClass } from "components/formFields/line/LineClass";
import { LineDescription } from "components/formFields/line/LineDescription";
import { LineInventoryItemBin } from "components/formFields/line/LineInventoryItemBin";
import { LineJobWorkcenter } from "components/formFields/line/LineJobWorkcenter";
import { LineLinkedTransaction } from "components/formFields/line/LineLinkedTransaction";
import { LineLotWithExpiration } from "components/formFields/line/LineLotWithExpiration";
import { LineQuantity } from "components/formFields/line/LineQuantity";
import { LineSerialsEnter } from "components/formFields/line/LineSerialsEnter";
import { LineUom } from "components/formFields/line/LineUom";
import { LineWaste } from "components/formFields/line/LineWaste";

import { i18n } from "services/i18nService";
import {
  getItemRecord,
  clearItemDataFromLine,
} from "services/sosInventoryService/domainLogic";
import { OUTPUT_ITEM_CALCULATED_FIELDS } from "services/sosInventoryService/process/schema";
import { updateLineWithItem } from "services/sosInventoryService/productionTransaction/domainLogic";
import { setPageDirty } from "services/utility/edit";
import { isLineFieldInError } from "services/utility/errors";
import { isFinishedGoods } from "services/utility/jobWorkCenter";
import { TYPES } from "services/utility/schema";

import { usePlans } from "hooks/usePlans";

import { OBJECT_TYPES } from "appConstants";
import { OUTPUT_LINE_ITEM_FIELDS } from "editConfig";

const OBJECT_TYPE = OBJECT_TYPES.PROCESS.fullString;

// created this wrapper to make drag and drop code easier to see
export function LineItem(props) {
  return <LineItemDetails {...props} />;
}

function LineItemDetails(props) {
  const {
    record,
    line,
    items,
    itemsCount,
    classes,
    classesCount,
    lineHandler,
    bins,
    location,
    jobWorkcenters,
    addItem,
    lineItemsErrors: errors,
    lineReadOnly = false,
    handleDeleteLine,
  } = props;
  const { date, workcenter, autoSerialLotNumbering } = record;

  const [addNewItem, setAddNewItem] = useState(false);
  const [newItemName, setNewItemName] = useState("");
  const [itemLoading, setItemLoading] = useState(false);
  const { hasAtLeastPlusPlan } = usePlans();

  // SETTINGS
  function isInError(field) {
    return isLineFieldInError(field, line.lineNumber, errors);
  }

  function handleValueChange(fieldName, newValue) {
    let newLine;
    const lineItemFields = OUTPUT_LINE_ITEM_FIELDS[OBJECT_TYPE];
    if (lineItemFields[TYPES.decimal].includes(fieldName)) {
      newLine = { ...line, [fieldName]: new Decimal(newValue || 0) };
    } else {
      newLine = { ...line, [fieldName]: newValue };
    }
    lineHandler({ type: "update", updatedLine: newLine });
  }

  function handleCheckUpdate(fieldName, newValue) {
    const updatedLine = { ...line, [fieldName]: newValue };
    setPageDirty();
    lineHandler({ type: "update", updatedLine });
  }

  function handleUomChange(_, uom) {
    setPageDirty();
    const oldUom = line.uom;
    const updatedLine = { ...line, uom, oldUom };
    lineHandler({ type: "update", updatedLine });
  }

  async function handleItemChange(newItem, userInput) {
    if (!newItem) {
      const updatedLine = clearItemDataFromLine(line);
      setPageDirty();
      lineHandler({ type: "update", updatedLine });
      return;
    }
    if (newItem?.id === "add") {
      return openAddItem(userInput);
    }
    setItemLoading(true);
    const item = await getItemRecord(
      newItem.id,
      record.location?.id,
      date,
      OUTPUT_ITEM_CALCULATED_FIELDS
    );
    const updatedLine = await updateLineWithItem(item, line, record, "output");
    setPageDirty();
    lineHandler({ type: "update", updatedLine });
    setItemLoading(false);
  }

  async function onAddItem(item) {
    setAddNewItem(false);
    await handleItemChange(item);
    addItem();
  }

  function handleLinkedTransactionChange(_, newValue) {
    const updatedLine = { ...line, linkedTransaction: newValue };
    setPageDirty();
    lineHandler({ type: "update", updatedLine });
  }

  function handleDelete() {
    if (handleDeleteLine) {
      handleDeleteLine(line.lineNumber);
    } else {
      setPageDirty();
      lineHandler({ type: "delete", deleteAt: line.lineNumber });
    }
  }

  function openAddItem(userInput) {
    // handle "Add new item" in Item select input field; this does not change the
    // line item, so no need to proceed further into the handler
    setNewItemName(userInput);
    setAddNewItem(true);
  }

  if (!line) {
    return null;
  }

  return (
    <>
      <DeleteLine onClick={handleDelete} />
      <LineTableCell>
        <div
          style={{
            cursor: "pointer",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "0 0.5rem",
          }}
        >
          {line.lineNumber}
        </div>
      </LineTableCell>
      <LineInventoryItemBin
        itemLoading={itemLoading}
        bins={bins}
        location={location}
        handleItemChange={handleItemChange}
        handleReferenceChange={handleValueChange}
        objectType={OBJECT_TYPE}
        line={line}
        items={items}
        itemsCount={itemsCount}
        addItem={addItem}
        isInError={isInError}
        lineReadOnly={lineReadOnly}
      />
      <LineTableCell sx={{ minWidth: "24rem" }}>
        <LineDescription
          value={line.description}
          onValueChange={handleValueChange}
          error={isInError("description")}
          lineReadOnly={lineReadOnly}
        />
        {isFinishedGoods(workcenter) &&
          !autoSerialLotNumbering &&
          hasAtLeastPlusPlan && (
            <>
              {line.itemDetails?.serialTracking && (
                <LineSerialsEnter
                  value={line.serials}
                  onValueChange={handleValueChange}
                  quantity={line.quantity}
                  error={isInError("serials")}
                  lineReadOnly={lineReadOnly}
                />
              )}
              {line.itemDetails?.lotTracking && (
                <LineLotWithExpiration
                  lot={line.lot}
                  lotExpiration={line.lotExpiration}
                  onValueChange={handleValueChange}
                  itemLotTracked={line.itemDetails?.lotTracking}
                  quantity={line.quantity}
                  lotError={isInError("lot")}
                  lotExpirationError={isInError("lotExpiration")}
                  lineReadOnly={lineReadOnly}
                />
              )}
            </>
          )}
      </LineTableCell>
      <LineQuantity
        objectType={OBJECT_TYPE}
        value={line.quantity}
        onValueChange={handleValueChange}
        error={isInError("quantity")}
        disabled={itemLoading}
        lineReadOnly={lineReadOnly}
      />
      <LineUom
        value={line.uom}
        onValueChange={handleUomChange}
        itemUoms={line.itemDetails?.itemUoms}
        error={isInError("uom")}
        lineReadOnly={lineReadOnly}
      />
      <LineLinkedTransaction
        objectType={OBJECT_TYPE}
        transactionLabel={i18n("workOrder.labelAbbreviatedNumber")}
        value={line.linkedTransaction}
        onValueChange={handleLinkedTransactionChange}
        error={isInError("linkedTransaction")}
        lineReadOnly={lineReadOnly}
      />
      <LineWaste
        value={line.waste}
        onValueChange={handleCheckUpdate}
        lineReadOnly={lineReadOnly}
      />

      <LineClass
        value={line.class}
        classes={classes}
        classesCount={classesCount}
        onValueChange={handleValueChange}
        lineReadOnly={lineReadOnly}
      />
      <LineJobWorkcenter
        job={line.job}
        workcenter={line.workcenter}
        value={line.jobWorkcenter}
        jobWorkcenters={jobWorkcenters}
        onValueChange={handleValueChange}
        lineReadOnly={lineReadOnly}
      />
      {addNewItem && (
        <QuickAddItem
          open={addNewItem}
          initialNameValue={newItemName}
          onClose={() => setAddNewItem(false)}
          onAdd={onAddItem}
        />
      )}
    </>
  );
}
